/**
 * Cable profiles -- group 11.
 *
 * MCRU, IsoTek, ISOL-8, Elrod Power Systems, Silversmith Audio,
 * Stage III Concepts and Silent Source.
 *
 * Six of these seven are mains-specialist or boutique houses, which has two
 * consequences for this file. First, none of them publishes per-metre
 * resistance, inductance or capacitance, so every entry here lets the kit
 * derive those three numbers from gauge, conductor material and geometry --
 * there is not one MANUFACTURER_SPEC electrical block in the file, because
 * there is not one published figure to put in it.
 *
 * Second, the majority of these products are power cords, and the engine
 * applies no signal processing to a power cord. That is not a gap in the
 * database: it is the correct answer. A 6000 dollar Elrod Statement or an
 * IsoTek Ascension can be a genuinely well made piece of electrical hardware --
 * heavy conductors, good plug bodies, a real current rating -- and still sit
 * entirely upstream of the rectifier and regulation of whatever it feeds. The
 * notes below describe what was built. They do not describe a sound.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

export const CABLES_11: CableProfile[] = [
  // =========================================================================
  // MCRU -- Huddersfield, England. Mains leads built to order around bought-in
  // bulk cable and Furutech / MS HD Power / Oyaide connector bodies.
  // =========================================================================

  makeCable({
    mfr: 'MCRU',
    model: 'No.75 Mains Cable',
    cableType: 'power',
    price: usd(180, 'APPROXIMATE', 2024, 'Approximate USD equivalent of the UK retail price for a 1.5 m lead.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 13,
    termination: ['IEC C13', 'UK 13A'],
    confidence: 'ESTIMATED',
    accent: '#7a9e3f',
    tags: ['uk-mains', 'entry-level', 'built-to-order'],
    notes:
      'The lead that made the company: bulk UK-made mains cable of roughly 2.5 sq mm per core, cut to length and fitted with a moulded 13 A plug and a screw-assembled IEC body. Three cores, live, neutral and earth, laid up with a slow twist. Everything the engine does with a power cord is nothing at all, so what this entry contributes to a chain is its price and its description.',
    provenanceNote:
      'MCRU build mains leads to order rather than publishing datasheets. Conductor size and current rating here are read from the UK retail listing; nobody publishes resistance, inductance or capacitance for this cable and none are invented.',
  }),

  makeCable({
    mfr: 'MCRU',
    model: 'No.75 Mk2 Mains Cable',
    cableType: 'power',
    price: usd(290, 'APPROXIMATE', 2024, 'Approximate USD equivalent of the UK retail price for a 1.5 m lead.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 13,
    termination: ['IEC C13', 'IEC C15', 'UK 13A', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#89ad4a',
    tags: ['uk-mains', 'rhodium-plated-contacts', 'built-to-order'],
    notes:
      'The revised No.75: heavier cores than the original and higher grade plug and IEC bodies with plated rather than bare brass contacts. Plating on a mains contact is a corrosion and contact-resistance measure at a joint carrying amps, which is a maintenance argument rather than an audio one. No processing is applied to power cords here.',
    provenanceNote:
      'Built to order, so conductor size and connector choice vary with the options taken at purchase. Figures below describe the standard build as listed at UK retail. No electrical measurements are published by the maker.',
  }),

  makeCable({
    mfr: 'MCRU',
    model: 'Ultimate Mains Cable',
    cableType: 'power',
    price: usd(950, 'APPROXIMATE', 2024, 'Approximate USD equivalent of the UK retail price for a 1.5 m lead.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Foil and braid over the laid-up cores',
    termination: ['IEC C15', 'IEC C19', 'UK 13A', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#a8c25c',
    tags: ['uk-mains', 'shielded', 'flagship', 'built-to-order'],
    notes:
      'MCRU\'s top build: continuous-cast copper cores in a shielded jacket, terminated with the heaviest connector bodies they stock. The shield is bonded at the plug end so that it drains to safety earth rather than floating, which is the correct way to do it on a mains lead and matters for RF ingress into the cabinet, not for the audio path. The engine leaves the samples untouched.',
    provenanceNote:
      'Assembled to order from bought-in bulk cable and third-party connectors, so this entry describes a representative build rather than a fixed catalogue part. Placement at the top of the MCRU range is from retail listings, not a manufacturer datasheet.',
  }),

  makeCable({
    mfr: 'MCRU',
    model: 'Custom Built Mains Cable',
    cableType: 'power',
    price: usd(350, 'APPROXIMATE', 2024, 'Approximate USD equivalent of a typical made-to-order 1.5 m build.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 13,
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#6f8f38',
    tags: ['uk-mains', 'built-to-order', 'configurable'],
    notes:
      'The made-to-order service rather than a fixed model: the buyer picks bulk cable, plug body and IEC type, and MCRU assemble and test it. Useful when a component needs a C15 for a hot inlet or a C19 for a large amplifier, which is a mechanical and current-rating decision. There is no signal path here for the engine to model.',
    provenanceNote:
      'A configurable build, not a single product, so gauge, current rating and connectors below represent a common mid-range specification. Prices vary widely with the options chosen.',
  }),

  // =========================================================================
  // IsoTek -- Winchester, England. Power products only: conditioners, blocks
  // and mains cables. No signal cables in the range, so none are invented.
  // =========================================================================

  makeCable({
    mfr: 'IsoTek',
    model: 'EVO3 Initium',
    cableType: 'power',
    price: usd(110, 'APPROXIMATE', 2023, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 13,
    termination: ['IEC C13', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f6fb2',
    tags: ['entry-level', 'evo3'],
    notes:
      'The bottom of the EVO3 mains range: three OFC cores of about 2 sq mm, PVC insulation, moulded plug and IEC bodies. It exists to replace the freebie cord in the box with something of a known gauge and a stated rating. Power cords receive no processing in this engine, so selecting it changes the chain description and the total price and nothing in the audio.',
  }),

  makeCable({
    mfr: 'IsoTek',
    model: 'EVO3 Premier',
    cableType: 'power',
    price: usd(230, 'APPROXIMATE', 2023, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 13,
    termination: ['IEC C13', 'IEC C15', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2a7dbf',
    tags: ['evo3', 'ofc'],
    notes:
      'A step up in cross-section from the Initium with the same three-core layout and a heavier jacket. IsoTek quote conductor area in square millimetres rather than AWG; the gauge recorded here is the nearest equivalent. Nothing about a mains cord is in the signal path and nothing is applied to it.',
  }),

  makeCable({
    mfr: 'IsoTek',
    model: 'EVO3 Sequel',
    cableType: 'power',
    price: usd(450, 'APPROXIMATE', 2023, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Foil wrap over the laid-up cores',
    termination: ['IEC C13', 'IEC C15', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3489cb',
    tags: ['evo3', 'shielded'],
    notes:
      'The first shielded cord in the EVO3 ladder and the first rated at 16 A, which is the point at which it becomes sensible for an integrated amplifier rather than a source. Shielding on a mains lead addresses radiated pickup around the cable, not the audio signal, and the engine applies no processing here.',
  }),

  makeCable({
    mfr: 'IsoTek',
    model: 'EVO3 Elite',
    cableType: 'power',
    price: usd(800, 'APPROXIMATE', 2023, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Foil and braid',
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f95d6',
    tags: ['evo3', 'shielded', 'high-current'],
    notes:
      'Around 4 sq mm per core with a double shield and a C19 option for high-inlet-current amplifiers. Heavier cores mean less voltage drop under the peak current a large amplifier pulls on transients, which is a mains-side engineering statement and the only kind this database will make about a power cord.',
  }),

  makeCable({
    mfr: 'IsoTek',
    model: 'EVO3 Optimum',
    cableType: 'power',
    price: usd(1700, 'APPROXIMATE', 2023, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 16,
    shielding: 'Multi-layer foil and braid',
    termination: ['IEC C15', 'IEC C19', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4aa1e0',
    tags: ['evo3', 'occ', 'shielded', 'high-current'],
    notes:
      'Continuous-cast copper cores of roughly 5 sq mm in a multi-layer shield, with connector bodies heavy enough that the cord needs supporting near the inlet. Continuous casting changes grain boundaries, not bulk resistivity: OCC and OFC copper differ by well under one percent in ohms per metre, and the kit treats them accordingly. No processing is applied to power cords.',
  }),

  makeCable({
    mfr: 'IsoTek',
    model: 'EVO3 Ascension',
    cableType: 'power',
    price: usd(4000, 'APPROXIMATE', 2023, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'concentric',
    gauge: 10,
    currentRatingA: 16,
    shielding: 'Multi-layer foil and braid with a drain to earth',
    termination: ['IEC C15', 'IEC C19', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5aade9',
    tags: ['evo3', 'flagship', 'silver-plated', 'shielded'],
    notes:
      'The EVO3 flagship: silver-plated continuous-cast cores wound concentrically inside a heavy multi-layer shield, in a jacket about as thick as a garden hose. At mains frequency the silver plating carries essentially none of the current -- skin depth at 50 Hz in copper is around 9 mm, so the whole cross-section conducts and the plating is a surface and corrosion measure. Zero DSP is applied for this or any other cord.',
  }),

  makeCable({
    mfr: 'IsoTek',
    model: 'EVO3 Syncro Uni',
    cableType: 'power',
    price: usd(1150, 'APPROXIMATE', 2023, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 10,
    shielding: 'Foil and braid',
    termination: ['IEC C13', 'IEC C15', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#66b8ef',
    tags: ['evo3', 'dc-blocking', 'active-network'],
    notes:
      'A shielded cord with a network moulded into the plug end that removes DC offset from the incoming mains. DC on the supply pushes a toroidal transformer towards saturation and makes it hum mechanically, so this is a real and measurable function -- performed on the mains, in front of the component, on a quantity the audio signal never sees. The engine still applies nothing to the signal path. Current rating is lower than the plain cords because the network sits in series with the live conductor.',
    provenanceNote:
      'Construction and the DC-blocking function are from IsoTek published descriptions. Current rating is the figure quoted for the in-line network rather than for the bare cable. No R, L or C figures are published.',
  }),

  makeCable({
    mfr: 'IsoTek',
    model: 'V5 Initium',
    cableType: 'power',
    price: usd(150, 'APPROXIMATE', 2024, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 13,
    termination: ['IEC C13', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#1a5f9e',
    tags: ['v5', 'entry-level'],
    notes:
      'The V5 generation entry cord, replacing the EVO3 Initium at the bottom of the range with new moulding and connector bodies and a broadly similar three-core OFC construction. As with every mains lead in this database, the engine applies no processing.',
    provenanceNote:
      'The V5 range supersedes EVO3; both generations are listed here because both are widely in circulation. Conductor sizes for V5 are quoted by IsoTek in square millimetres and the AWG figure is the nearest equivalent.',
  }),

  makeCable({
    mfr: 'IsoTek',
    model: 'V5 Premier',
    cableType: 'power',
    price: usd(300, 'APPROXIMATE', 2024, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 13,
    termination: ['IEC C13', 'IEC C15', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#246bab',
    tags: ['v5', 'ofc'],
    notes:
      'Second rung of the V5 mains range. Three OFC cores, moulded ends, a modest step up in cross-section from the Initium. Build only; there is no audio path in a mains cord for the engine to touch.',
    provenanceNote:
      'V5 is the current generation of the IsoTek cable range. Construction detail is from retail listings rather than a datasheet, and no electrical figures are published.',
  }),

  makeCable({
    mfr: 'IsoTek',
    model: 'V5 Sequel',
    cableType: 'power',
    price: usd(600, 'APPROXIMATE', 2024, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Foil wrap over the laid-up cores',
    termination: ['IEC C13', 'IEC C15', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#2e77b8',
    tags: ['v5', 'shielded'],
    notes:
      'Shielded 16 A cord in the current V5 generation, sized for integrated amplifiers and conditioners rather than sources. Shield, gauge and rating are the substance of it, and none of them reach the signal.',
    provenanceNote:
      'Current-generation replacement for the EVO3 Sequel. Construction detail is from retail listings; IsoTek do not publish per-metre electrical figures for any cable.',
  }),

  makeCable({
    mfr: 'IsoTek',
    model: 'V5 Elite',
    cableType: 'power',
    price: usd(1000, 'APPROXIMATE', 2024, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Foil and braid',
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#3883c4',
    tags: ['v5', 'shielded', 'high-current'],
    notes:
      'Roughly 4 sq mm cores, double shield, C19 available. The gauge is the useful number: at 11 AWG the loop resistance of a 1.5 m cord is a couple of tens of milliohms, so the drop across it at 5 A is on the order of a tenth of a volt. That is a mains-side figure and the engine does not convert it into anything.',
    provenanceNote:
      'Current-generation replacement for the EVO3 Elite. Conductor area from retail listings, converted to the nearest AWG. No manufacturer R, L or C figures exist.',
  }),

  makeCable({
    mfr: 'IsoTek',
    model: 'V5 Optimum',
    cableType: 'power',
    price: usd(2200, 'APPROXIMATE', 2024, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 16,
    shielding: 'Multi-layer foil and braid',
    termination: ['IEC C15', 'IEC C19', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#428fd0',
    tags: ['v5', 'occ', 'shielded', 'high-current'],
    notes:
      'Continuous-cast copper, about 5 sq mm per core, multi-layer shield, heavy connector bodies. A properly specified high-current cord. It is also, from the engine\'s point of view, inert: power cords are passed through untouched by design.',
    provenanceNote:
      'Current-generation replacement for the EVO3 Optimum. Construction from retail listings; no published electrical data.',
  }),

  makeCable({
    mfr: 'IsoTek',
    model: 'V5 Ascension',
    cableType: 'power',
    price: usd(5000, 'APPROXIMATE', 2024, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'concentric',
    gauge: 10,
    currentRatingA: 16,
    shielding: 'Multi-layer foil and braid with a drain to earth',
    termination: ['IEC C15', 'IEC C19', 'UK 13A', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#4d9bdb',
    tags: ['v5', 'flagship', 'silver-plated', 'shielded'],
    notes:
      'Top of the current IsoTek range: silver-plated continuous-cast cores in a concentric lay, heavily shielded, in a jacket that dictates how far the component has to sit from the wall. Excellent mains hardware, and the DSP contribution of selecting it is exactly zero. Both facts are stated deliberately.',
    provenanceNote:
      'Current-generation replacement for the EVO3 Ascension. Construction from retail listings and IsoTek published descriptions; no per-metre electrical figures are published.',
  }),

  // =========================================================================
  // ISOL-8 -- Surrey, England. Predominantly a power conditioning and
  // isolation-transformer house; the mains lead below is the one cable in the
  // range that could be confirmed, so it is the only one listed.
  // =========================================================================

  makeCable({
    mfr: 'Isol-8',
    model: 'IsoLink Ultra',
    cableType: 'power',
    price: usd(600, 'APPROXIMATE', 2023, 'Approximate USD equivalent of the UK retail price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Screened, drained to safety earth at the plug end',
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'UK 13A', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#4a5a6a',
    tags: ['uk-mains', 'shielded', 'conditioner-brand'],
    notes:
      'A screened three-core mains lead intended to feed ISOL-8\'s own conditioners and blocks. The screen is drained at one end only, which is the standard way to avoid making the shield a second earth path, and it is done for RF and safety reasons rather than sonic ones. As with every power cord in this database the engine applies no processing whatsoever.',
    provenanceNote:
      'ISOL-8 is primarily a power conditioning manufacturer and publishes little cable detail. Gauge, screening arrangement and rating here are inferred from retail listings and from the company\'s own installation guidance; no electrical figures are published.',
  }),

  // =========================================================================
  // Elrod Power Systems -- United States. Power cords, in a numbered ladder
  // from source-component cords up to very heavy amplifier cords. No longer in
  // general production; prices below are what they sold for new.
  // =========================================================================

  makeCable({
    mfr: 'Elrod Power Systems',
    model: 'EPS-1',
    cableType: 'power',
    price: usd(400, 'ORIGINAL_RETAIL', 2006, 'Original retail for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#8c6239',
    tags: ['source-component', 'discontinued', 'us-made'],
    notes:
      'The entry cord in the Elrod ladder, intended for source components and preamplifiers where the current draw is small and the cord mostly has to be mechanically sensible. Multiple stranded copper conductors per leg, laid up with a twist, in a flexible jacket. No signal processing is applied to power cords.',
    provenanceNote:
      'Elrod Power Systems is no longer in general production and never published construction data in detail. Gauge and geometry here are inferred from the cord\'s physical size and from period reviews and dealer listings; the price is original retail, not a current market figure.',
  }),

  makeCable({
    mfr: 'Elrod Power Systems',
    model: 'EPS-2',
    cableType: 'power',
    price: usd(700, 'ORIGINAL_RETAIL', 2006, 'Original retail for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 15,
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#95693e',
    tags: ['discontinued', 'us-made'],
    notes:
      'Middle of the numbered range, sized for integrated amplifiers and digital front ends. Heavier conductor bundles than the EPS-1 and a correspondingly stiffer jacket. The whole of what this entry asserts is the build; the engine leaves the audio untouched for cords.',
    provenanceNote:
      'Out of production. Construction inferred from period reviews and dealer listings rather than a manufacturer datasheet; price is original retail.',
  }),

  makeCable({
    mfr: 'Elrod Power Systems',
    model: 'EPS-3',
    cableType: 'power',
    price: usd(1300, 'ORIGINAL_RETAIL', 2006, 'Original retail for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 10,
    currentRatingA: 20,
    termination: ['IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#9e7144',
    tags: ['high-current', 'amplifier-cord', 'discontinued', 'us-made'],
    notes:
      'The amplifier cord: about 10 AWG per leg in a woven bundle, thick enough that it was normally supported rather than left hanging off an inlet. At that gauge a 1.8 m cord has a loop resistance around 12 milliohms, so the drop feeding a 10 A transient is on the order of 0.1 V out of 120. That is the honest scale of what heavy mains cable does, and it happens upstream of the rectifier.',
    provenanceNote:
      'Out of production. Gauge, geometry and current rating are inferred from the cord\'s physical dimensions and period listings; Elrod published no electrical data. Price is original retail.',
  }),

  makeCable({
    mfr: 'Elrod Power Systems',
    model: 'EPS-1 Signature',
    cableType: 'power',
    price: usd(900, 'ORIGINAL_RETAIL', 2008, 'Original retail for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 15,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#a97b4d',
    tags: ['signature-series', 'shielded', 'discontinued', 'us-made'],
    notes:
      'The Signature revision of the entry cord: shielded, with heavier conductor bundles and better connector bodies than the plain EPS-1. Source-component duty. No DSP is applied for power cords and none is implied.',
    provenanceNote:
      'Out of production. The Signature series revised conductor count and shielding; specifics here are inferred from period reviews and dealer listings. Price is original retail.',
  }),

  makeCable({
    mfr: 'Elrod Power Systems',
    model: 'EPS-2 Signature',
    cableType: 'power',
    price: usd(1600, 'ORIGINAL_RETAIL', 2008, 'Original retail for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#b28457',
    tags: ['signature-series', 'shielded', 'high-current', 'discontinued'],
    notes:
      'Shielded Signature cord with the conductors woven rather than simply twisted. Weaving opposing runs cancels part of the external field, which on a mains lead is about what the cord radiates into its neighbours rather than about the audio. Nothing is applied to the signal path.',
    provenanceNote:
      'Out of production. Construction inferred from period reviews and the cord\'s physical build; no manufacturer electrical data exists. Price is original retail.',
  }),

  makeCable({
    mfr: 'Elrod Power Systems',
    model: 'EPS-3 Signature',
    cableType: 'power',
    price: usd(2800, 'ORIGINAL_RETAIL', 2008, 'Original retail for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Shielded',
    termination: ['IEC C19', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#bb8d61',
    tags: ['signature-series', 'flagship-adjacent', 'high-current', 'discontinued'],
    notes:
      'One of the heaviest cords of its era: a woven bundle of large copper conductors in a shielded jacket, usually specified with a C19 because a C13 inlet could not take the mass. Everything about it is mechanical and electrical on the mains side. The engine models power cords as identity.',
    provenanceNote:
      'Out of production. Gauge and geometry inferred from physical build and period listings; Elrod published no per-metre figures. Price is original retail.',
  }),

  makeCable({
    mfr: 'Elrod Power Systems',
    model: 'Statement Gold',
    cableType: 'power',
    price: usd(6000, 'APPROXIMATE', 2012, 'Approximate original retail for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Multi-layer shield',
    termination: ['IEC C19', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#c69a6a',
    tags: ['statement-series', 'flagship', 'high-current', 'discontinued'],
    notes:
      'Elrod\'s flagship cord: a very large woven copper bundle, multi-layer shielding and gold-plated connector hardware, at a price that put it among the most expensive mains leads sold in its day. Worth being plain about what this entry does in the simulation: it adds a line to the chain description and 6000 dollars to the total, and applies no processing at all, because there is no mechanism by which a cord ahead of a rectifier alters the samples.',
    provenanceNote:
      'Out of production. The Statement name refers to the model line and its plating; the conductors are copper. Construction inferred from period reviews and dealer listings; price is approximate original retail.',
  }),

  makeCable({
    mfr: 'Elrod Power Systems',
    model: 'Statement Silver',
    cableType: 'power',
    price: usd(4200, 'APPROXIMATE', 2012, 'Approximate original retail for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'braided',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Multi-layer shield',
    termination: ['IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#b9aa96',
    tags: ['statement-series', 'silver-plated', 'high-current', 'discontinued'],
    notes:
      'Statement construction with silver-plated copper conductors. Plating matters at radio frequencies where current crowds into the surface; at 50 or 60 Hz the skin depth in copper is many millimetres and the entire conductor carries current, so the plating is a contact and corrosion measure here. The engine applies nothing to power cords.',
    provenanceNote:
      'Out of production. Conductor plating and construction inferred from period reviews and dealer listings; no manufacturer electrical data. Price is approximate original retail.',
  }),

  // =========================================================================
  // Silversmith Audio -- Florida, United States. Flat-conductor speaker cables
  // and interconnects. Note the conductor-material caveats below: neither the
  // Palladium nor the Fidelium conductor has a matching entry in the schema's
  // material list, and both entries say so rather than quietly picking one.
  // =========================================================================

  makeCable({
    mfr: 'Silversmith Audio',
    model: 'Fidelium',
    cableType: 'speaker',
    price: usd(1200, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m pair; Silversmith price by length.'),
    priceLengthM: 2.5,
    year: 2020,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'ribbon',
    gauge: 16,
    dielectric: 'Thin polymer film over a flat ribbon, largely air-spaced in use',
    termination: ['spade', 'banana', 'bare'],
    defaultLengthM: 2.5,
    confidence: 'ESTIMATED',
    accent: '#b8bcc0',
    tags: ['ribbon', 'flat-conductor', 'proprietary-alloy', 'material-not-in-schema'],
    notes:
      'A very thin, wide flat ribbon per polarity rather than a bundle of round strands. Two consequences follow from the shape alone. Loop inductance is roughly a tenth of an ordinary twisted pair, because the conductors are wide and close; and capacitance is correspondingly high, on the order of a nanofarad and a half over a 2.5 m pair, which is worth knowing if the amplifier is one of the feedback designs that dislikes a capacitive load at its terminals and specifies a series inductor or a minimum cable inductance. The ribbon\'s cross-section is equivalent to about 16 AWG, so a 2.5 m run adds around 0.07 ohm of loop resistance: about 0.17 dB of level difference between a 3 ohm dip and a 30 ohm peak in the loudspeaker\'s impedance curve. That divider is what the engine computes.',
    provenanceNote:
      'Silversmith state that the Fidelium conductor is a proprietary alloy which is neither copper nor silver, and do not disclose its composition. The schema\'s material list has no entry for it, so OFC is recorded purely as a resistivity placeholder for the derivation and the real alloy is flagged in the tags. Treat the derived resistance as indicative. No manufacturer R, L or C figures are published.',
  }),

  makeCable({
    mfr: 'Silversmith Audio',
    model: 'Fidelium Jumpers',
    cableType: 'speaker',
    price: usd(325, 'APPROXIMATE', 2024, 'Approximate price for a set of four jumpers.'),
    priceLengthM: 0.25,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'ribbon',
    gauge: 16,
    termination: ['spade', 'banana'],
    standardLengthsM: [0.15, 0.2, 0.25, 0.3],
    defaultLengthM: 0.25,
    confidence: 'ESTIMATED',
    accent: '#c4c8cc',
    tags: ['ribbon', 'jumpers', 'bi-wire-terminals', 'proprietary-alloy'],
    notes:
      'Short Fidelium ribbons made to replace the stamped brass straps supplied with bi-wirable loudspeakers. The engineering case is straightforward and small: a brass strap is a few milliohms of a poor conductor across the crossover terminals, and a 200 mm ribbon of the same alloy as the main cable is fewer. On a 4 ohm load the difference is a small fraction of a decibel, and the engine will report whatever it works out to rather than more.',
    provenanceNote:
      'Sold as an accessory set rather than by the metre; the length recorded is a typical jumper length. Conductor material is the same undisclosed proprietary alloy as the Fidelium cable, recorded here with a placeholder material as explained on that entry.',
  }),

  makeCable({
    mfr: 'Silversmith Audio',
    model: 'Palladium Speaker Cable',
    cableType: 'speaker',
    price: usd(18000, 'ORIGINAL_RETAIL', 2010, 'Original retail for a 2.5 m pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'ribbon',
    gauge: 16,
    dielectric: 'Air and thin film over flat ribbon conductors',
    termination: ['spade', 'banana'],
    defaultLengthM: 2.5,
    confidence: 'ESTIMATED',
    accent: '#9aa0a6',
    tags: ['ribbon', 'palladium-ribbon', 'material-not-in-schema', 'discontinued'],
    notes:
      'Silversmith\'s previous flagship, built from flat palladium ribbon rather than copper or silver. The flat-ribbon geometry gives the same low-inductance, high-capacitance balance as the later Fidelium. The conductor metal is the unusual part and it has a real electrical cost: palladium\'s resistivity is roughly six times that of copper, so a ribbon of a given cross-section has around six times the series resistance, which on a low-impedance loudspeaker is the one figure that actually shows up in the divider.',
    provenanceNote:
      'The conductor is palladium ribbon. The schema\'s material list has no palladium entry, so pure silver is recorded as the nearest available option; because palladium is about six times more resistive than silver, the loop resistance derived for this entry UNDERSTATES the real figure by roughly that factor. The tags flag the real metal. Discontinued; price is original retail.',
  }),

  makeCable({
    mfr: 'Silversmith Audio',
    model: 'Palladium Interconnect',
    cableType: 'interconnect',
    price: usd(5000, 'ORIGINAL_RETAIL', 2010, 'Original retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'flat-parallel',
    gauge: 24,
    dielectric: 'Air and thin film',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#8f959b',
    tags: ['flat-conductor', 'palladium-ribbon', 'material-not-in-schema', 'discontinued'],
    notes:
      'The line-level version of the Palladium ribbon, two flat conductors run parallel. That geometry puts capacitance near 300 pF per metre, which is high for an interconnect and is the number worth checking: against a 600 ohm solid-state output a 1 m run corners near 880 kHz, comfortably out of the way, but against the 5 kilohm or so a passive volume control can present at mid-rotation it falls to about 100 kHz, where the top octave starts to see a fraction of a decibel. Series resistance is irrelevant at line level.',
    provenanceNote:
      'Conductor is palladium ribbon; as on the speaker cable entry, the schema has no palladium material so pure silver stands in for the derivation and the derived resistance is optimistic by roughly six times. Series resistance does not matter into a line input, so the substitution has no practical consequence here. Discontinued; price is original retail.',
  }),

  // =========================================================================
  // Stage III Concepts -- Poland. Large air-dielectric designs. Only the model
  // names that could be confirmed are listed; the range is broader than this.
  // =========================================================================

  makeCable({
    mfr: 'Stage III Concepts',
    model: 'Kraken',
    cableType: 'power',
    price: usd(6000, 'APPROXIMATE', 2022, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Poland',
    conductor: 'silver-gold-alloy',
    geometry: 'air-tube',
    gauge: 10,
    currentRatingA: 20,
    dielectric: 'PTFE air tubes',
    shielding: 'Braided shield drained at the plug end',
    termination: ['IEC C15', 'IEC C19', 'Schuko', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#a03050',
    tags: ['air-dielectric', 'high-current', 'flagship-range'],
    notes:
      'A very large mains cord built the way the company builds its signal cables: conductors suspended in PTFE tubes so that most of the dielectric is air, inside a braided shield, in a jacket thick enough to need dressing. Air dielectric is a capacitance-lowering measure, which is a real design goal in a signal cable and an irrelevant one in a mains lead, where the cord\'s job is to carry current with little drop and to not radiate. The engine applies no processing to power cords.',
    provenanceNote:
      'Stage III Concepts publish descriptive construction detail rather than measurements. Conductor alloy, gauge and rating here are inferred from distributor listings and reviews, not from a datasheet, and no per-metre electrical figures exist.',
  }),

  makeCable({
    mfr: 'Stage III Concepts',
    model: 'Leviathan',
    cableType: 'speaker',
    price: usd(12000, 'APPROXIMATE', 2022, 'Approximate retail for a 2.5 m pair.'),
    priceLengthM: 2.5,
    country: 'Poland',
    conductor: 'silver-gold-alloy',
    geometry: 'air-tube',
    gauge: 10,
    dielectric: 'PTFE air tubes',
    termination: ['spade', 'banana'],
    defaultLengthM: 2.5,
    confidence: 'ESTIMATED',
    accent: '#b03a5c',
    tags: ['air-dielectric', 'flagship-range', 'low-capacitance'],
    notes:
      'Heavy silver-gold alloy conductors held apart in air-filled PTFE tubes. The wide spacing is what gives the low capacitance the construction is aimed at, and by the same geometry it gives high inductance -- these two always move in opposite directions. Over a 2.5 m pair that works out near 2 uH, about 0.28 ohm of reactance at 20 kHz, which against a 4 ohm load is roughly 0.02 dB of extra top-octave loss on top of the 0.04 dB the 10 AWG conductors already cost. Small, computable, and reported as it comes out.',
    provenanceNote:
      'Model placement within the Stage III range and the cable type are taken from distributor listings rather than a manufacturer datasheet. Conductor alloy and gauge are inferred from published descriptions; no electrical figures are published.',
  }),

  makeCable({
    mfr: 'Stage III Concepts',
    model: 'Poseidon',
    cableType: 'interconnect',
    price: usd(4500, 'APPROXIMATE', 2022, 'Approximate retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Poland',
    conductor: 'silver-gold-alloy',
    geometry: 'air-tube',
    gauge: 20,
    dielectric: 'PTFE air tubes',
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#c04466',
    tags: ['air-dielectric', 'low-capacitance', 'balanced'],
    notes:
      'Line-level cable using the same air-tube construction. The consequence is a capacitance near 28 pF per metre, roughly a third of a typical shielded twisted pair: a 1 m run against a 1 kilohm tube output stage corners in the megahertz, which means the in-band effect the engine computes is a small fraction of a decibel, and it is a smaller fraction than for a conventional cable. That is the entire electrical claim being made.',
    provenanceNote:
      'Model placement within the Stage III range and the cable type are taken from distributor listings rather than a manufacturer datasheet. Conductor alloy and gauge are inferred from published construction descriptions; no per-metre electrical figures are published.',
  }),

  // =========================================================================
  // Silent Source -- United States. Two lines, Signature and The Music
  // Reference, each spanning interconnect, speaker and mains.
  // =========================================================================

  makeCable({
    mfr: 'Silent Source',
    model: 'Signature Interconnect',
    cableType: 'interconnect',
    price: usd(700, 'APPROXIMATE', 2020, 'Approximate retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided copper shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#2f6f5f',
    tags: ['signature-line', 'shielded', 'balanced'],
    notes:
      'A conventional shielded twisted pair of about 22 AWG, hand-terminated. Nothing exotic in the geometry, and the electrical result is the usual one for the type: near 70 pF per metre, so a 1 m run into a normal solid-state output corners several megahertz above the audio band and the engine reports an in-band effect of about 0.00 dB. That is what a competently built interconnect does.',
    provenanceNote:
      'Silent Source publish descriptive rather than numerical specifications. Gauge, geometry and shielding here are inferred from the cable\'s physical build and from retail listings; no R, L or C figures are published.',
  }),

  makeCable({
    mfr: 'Silent Source',
    model: 'Signature Speaker Cable',
    cableType: 'speaker',
    price: usd(1500, 'APPROXIMATE', 2020, 'Approximate retail for a 2.5 m pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana'],
    defaultLengthM: 2.5,
    confidence: 'ESTIMATED',
    accent: '#377a68',
    tags: ['signature-line', 'ofc'],
    notes:
      'Twelve-gauge copper laid up as a twisted pair. At that gauge a 2.5 m run contributes about 0.026 ohm of loop resistance, which across an impedance curve running from 3 to 30 ohms is under 0.07 dB of level variation -- the divider the engine actually computes. Inductance at 0.6 uH per metre adds a further hundredth of a decibel at 20 kHz into 4 ohms.',
    provenanceNote:
      'Construction inferred from the cable\'s physical build and retail listings; Silent Source publish no per-metre electrical figures.',
  }),

  makeCable({
    mfr: 'Silent Source',
    model: 'Signature Power Cord',
    cableType: 'power',
    price: usd(700, 'APPROXIMATE', 2020, 'Approximate retail for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#28614f',
    tags: ['signature-line', 'us-made'],
    notes:
      'Three-conductor 12 AWG mains cord with hand-assembled plug and IEC bodies. Rated for 15 A, which covers everything short of a large power amplifier on a dedicated 20 A line. Power cords receive no processing in this engine.',
    provenanceNote:
      'Construction inferred from retail listings and the cord\'s physical build; no manufacturer electrical data is published.',
  }),

  makeCable({
    mfr: 'Silent Source',
    model: 'The Music Reference Interconnect',
    cableType: 'interconnect',
    price: usd(2700, 'APPROXIMATE', 2020, 'Approximate retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided shield over a damped core',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#3d8a74',
    tags: ['music-reference-line', 'shielded', 'balanced', 'damped'],
    notes:
      'The senior Silent Source line-level cable: mixed copper and silver conductors in a twisted pair, with a damping layer between core and shield and a heavier jacket than the Signature. The damping is mechanical, aimed at handling noise and microphony at the connector, and it does not change the three numbers the engine reads. Those stay what a shielded twisted pair gives: about 70 pF per metre, a corner far above the audio band, and an in-band result near zero.',
    provenanceNote:
      'Silent Source describe conductor materials and damping but publish no measurements. Gauge and geometry are inferred from the cable\'s build and retail listings.',
  }),

  makeCable({
    mfr: 'Silent Source',
    model: 'The Music Reference Speaker Cable',
    cableType: 'speaker',
    price: usd(4800, 'APPROXIMATE', 2020, 'Approximate retail for a 2.5 m pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'braided',
    gauge: 11,
    termination: ['spade', 'banana'],
    defaultLengthM: 2.5,
    confidence: 'ESTIMATED',
    accent: '#469780',
    tags: ['music-reference-line', 'braided', 'low-inductance'],
    notes:
      'Mixed copper and silver conductors woven rather than twisted. The weave is the electrically meaningful part: adjacent runs carrying opposing current cancel much of the external field, which drops loop inductance to roughly 0.4 uH per metre against 0.6 for a plain pair, and raises capacitance in exchange. At 11 AWG a 2.5 m pair adds about 0.021 ohm, so the impedance-curve divider costs under 0.06 dB between a deep dip and a peak.',
    provenanceNote:
      'Conductor mix and weave are from Silent Source descriptions; gauge is inferred from the cable\'s cross-section. No per-metre electrical figures are published, so R, L and C here are derived.',
  }),

  makeCable({
    mfr: 'Silent Source',
    model: 'The Music Reference Power Cord',
    cableType: 'power',
    price: usd(1800, 'APPROXIMATE', 2020, 'Approximate retail for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'braided',
    gauge: 11,
    currentRatingA: 20,
    shielding: 'Shielded',
    termination: ['IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#1f5744',
    tags: ['music-reference-line', 'shielded', 'high-current'],
    notes:
      'Senior-line mains cord: woven mixed-metal conductors, shielded, 20 A rated, with connector bodies to match. A good piece of mains hardware whose contribution to the simulated signal is exactly nothing, because power cords sit ahead of the rectifier and reservoir of whatever they feed and the engine will not pretend otherwise.',
    provenanceNote:
      'Construction from Silent Source descriptions and retail listings; gauge inferred from the cord\'s cross-section. No electrical data is published.',
  }),

  makeCable({
    mfr: 'Silent Source',
    model: 'The Music Reference Digital Cable',
    cableType: 'interconnect',
    price: usd(1300, 'APPROXIMATE', 2020, 'Approximate retail for a 1 m cable.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'coaxial',
    gauge: 22,
    characteristicOhms: 75,
    shielding: 'Braided shield acting as the return conductor',
    termination: ['RCA', 'BNC'],
    digitalStandard: 'S/PDIF coaxial, 75 ohm',
    confidence: 'ESTIMATED',
    accent: '#54a48c',
    tags: ['music-reference-line', 'spdif', 'coaxial', '75-ohm'],
    notes:
      'A 75 ohm coaxial S/PDIF lead in the senior line. The specification that matters for this type is impedance match at both ends: a mismatch produces reflections on a signal whose fundamental is in the megahertz, which is a timing and eye-diagram question rather than a tonal one, and it is resolved by termination geometry rather than by conductor metal. Filed as an interconnect because that is the connector and impedance class; the engine computes the same R, L and C divider it computes for any line-level cable, which at 85 pF per metre is negligible.',
    provenanceNote:
      'Recorded as an interconnect because the schema has no separate digital-coaxial type. Construction from retail listings; no manufacturer electrical figures are published beyond the 75 ohm characteristic impedance the format requires.',
  }),
];
